/**
 * src/lib/voice/voiceOptions.js — Voice + language option registry (Phase 82-05).
 *
 * Single source of truth for the Voice Mode picker. Each voice alias (Nora,
 * Sofía, Marco, Elías) maps to a concrete `{ provider, voiceId }` tuple that
 * lines up with `ttsProvider.js` (openai-tts1 / openai-tts1-hd / gemini-3.1-flash-tts).
 *
 * Aliases are a frontend concept — the provider/voiceId tuple is what actually
 * ships to the TTS backend. If a provider's voice catalogue changes, adjust the
 * tuple here without renaming the alias (so localStorage stays stable).
 *
 * Languages are a hint to the STT/TTS pipeline:
 *   - 'auto' → no hint, provider auto-detects
 *   - 'en' | 'es' → explicit language for both STT and TTS
 *   - 'mix' → bilingual; Phase 76-03 view prompt bias handles the back-and-forth
 */

import { setStoredTtsProvider, TTS_PROVIDER_NAMES } from './ttsProvider.js';

const LS_VOICE_KEY = 'acn_voice_choice';
const LS_LANG_KEY = 'acn_voice_language';

// llm-registry-ok: src/ runs in Vite browser bundle — cannot use readFileSync-based getModel().
// provider values are TTS routing identifiers (not API model IDs); registry: voice_tts_gemini role → gemini-3.1-flash-tts-preview.
export const VOICES = [
  { id: 'nora', displayName: 'Nora', provider: 'openai-tts1', voiceId: 'nova', locale: 'en', mt: 'multilingual', default: true },
  { id: 'sofia', displayName: 'Sofía', provider: 'openai-tts1-hd', voiceId: 'shimmer', locale: 'es', mt: 'multilingual' },
  { id: 'marco', displayName: 'Marco', provider: 'gemini-3.1-flash-tts', voiceId: 'Charon', locale: 'es', mt: 'multilingual' }, // llm-registry-ok: provider routing key
  { id: 'elias', displayName: 'Elías', provider: 'openai-tts1', voiceId: 'onyx', locale: 'es', mt: 'multilingual' },
];

// 2026-04-21 — removed 'auto' and 'mix' from the picker. User directive:
// "no auto. English and Spanish". Whisper auto-detect on low-SNR audio
// drifts to Polish/Finnish/Ukrainian; 'mix' was a theoretical bilingual
// hint that never shipped reliably. Any localStorage value outside this
// list falls through to DEFAULT_LANGUAGE ('en') via getStoredLanguage's
// existing validation. No migration script needed.
export const LANGUAGES = [
  { id: 'en', displayName: 'English' },
  { id: 'es', displayName: 'Español' },
];

export const DEFAULT_VOICE_ID = 'nora';
// 2026-04-21 (C1/W4 root-cause fix) — default flipped from 'auto' to 'en'.
// When Whisper receives no language hint (language=undefined), it auto-detects
// on low-energy audio and emits foreign-language YouTube boilerplate (Russian
// "Поздравляю! С вами был Игорь Негода", Ukrainian "Дякую за перегляд!",
// vocab-list echo, etc.). Production evidence 2026-04-21 10:17 UTC in
// app_error_logs voice_error phrase_count:4x rejections. Users who want
// bilingual auto-detect can still pick 'auto' from the language picker.
export const DEFAULT_LANGUAGE = 'en';

export function resolveVoice(id) {
  const v = VOICES.find(x => x.id === id);
  if (!v) throw new Error(`Unknown voice: ${id}. Known: ${VOICES.map(x => x.id).join(', ')}`);
  if (!TTS_PROVIDER_NAMES.includes(v.provider)) {
    throw new Error(`Voice ${id} references unknown provider: ${v.provider}`);
  }
  return v;
}

export function resolveLanguage(id) {
  const l = LANGUAGES.find(x => x.id === id);
  if (!l) throw new Error(`Unknown language: ${id}. Known: ${LANGUAGES.map(x => x.id).join(', ')}`);
  return l;
}

export function getStoredVoiceId() {
  if (typeof localStorage === 'undefined') return DEFAULT_VOICE_ID;
  try {
    const v = localStorage.getItem(LS_VOICE_KEY);
    if (v && VOICES.some(x => x.id === v)) return v;
    return DEFAULT_VOICE_ID;
  } catch {
    return DEFAULT_VOICE_ID;
  }
}

export function setStoredVoiceId(id) {
  const voice = resolveVoice(id);
  if (typeof localStorage !== 'undefined') {
    try { localStorage.setItem(LS_VOICE_KEY, voice.id); } catch { /* noop */ }
  }
  // Also pipe the underlying provider to ttsProvider.js so agent.tts_stream
  // reads the matching provider on the next turn. Voice ID routing within the
  // provider happens server-side via agent.tts_stream body (future patch).
  try { setStoredTtsProvider(voice.provider); } catch { /* noop */ }
  return voice;
}

export function getStoredLanguage() {
  if (typeof localStorage === 'undefined') return DEFAULT_LANGUAGE;
  try {
    const v = localStorage.getItem(LS_LANG_KEY);
    if (v && LANGUAGES.some(x => x.id === v)) return v;
    return DEFAULT_LANGUAGE;
  } catch {
    return DEFAULT_LANGUAGE;
  }
}

export function setStoredLanguage(id) {
  const lang = resolveLanguage(id);
  if (typeof localStorage !== 'undefined') {
    try { localStorage.setItem(LS_LANG_KEY, lang.id); } catch { /* noop */ }
  }
  return lang;
}
